import * as moment from "moment-timezone";
import InventoryOperationEnum from "@models/InventoryOperationEnum";
import { IInventoryLogEntry } from "@models/DTO/IOnGetInventoryLogsDTO";

/** The API reads the log's date filters in this timezone when the user has none set (as ReportService does). */
export const INVENTORY_LOG_DEFAULT_TIMEZONE = "America/New_York";

/** The timezone the log is shown in: the same one the API reads the date filters in, so a day's rows match the filter. */
export function inventoryLogTimezone(prefTimezone: string | null | undefined): string {
    if (!prefTimezone) {
        return INVENTORY_LOG_DEFAULT_TIMEZONE;
    }

    return prefTimezone.toLowerCase() === "utc" ? "UTC" : prefTimezone;
}

/** e.g. "11/04/2026 3:07 PM EST". */
export function formatLogTimestamp(iso: string, timezone: string): string {
    const parsed = moment.tz(iso, timezone);

    return parsed.isValid() ? parsed.format("MM/DD/YYYY h:mm A z") : "";
}

/** e.g. "11/04/2026", for a PO shipment's received date. */
export function formatLogDate(iso: string | null, timezone: string): string {
    if (!iso) {
        return "";
    }

    const parsed = moment.tz(iso, timezone);

    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
}

/**
 * How much the entry moved the quantity, signed: ADD is +qty and SUBTRACT is -qty. A cycle count stores the
 * variance unsigned in qty, with start = expected and end = counted, so its sign comes from end - start.
 */
export function signedLogChange(entry: Pick<IInventoryLogEntry, "operationId" | "qty" | "startQtyOnHand" | "endQtyOnHand">): number {
    switch (Number(entry.operationId)) {
        case InventoryOperationEnum.ADD:
            return entry.qty;
        case InventoryOperationEnum.SUBTRACT:
            return -entry.qty;
        case InventoryOperationEnum.CYCLE_COUNT:
            if (entry.startQtyOnHand !== null && entry.endQtyOnHand !== null) {
                return entry.endQtyOnHand - entry.startQtyOnHand;
            }

            return entry.qty;
        default:
            return entry.qty;
    }
}

/** "+5", "-3" (with a real minus sign) or "0". */
export function formatSignedChange(change: number): string {
    if (change > 0) {
        return `+${change}`;
    }

    if (change < 0) {
        return `−${Math.abs(change)}`;
    }

    return "0";
}

/** "10 → 15", or the one side that was recorded, or "" when the entry recorded neither. */
export function formatBeforeAfter(start: number | null, end: number | null): string {
    if (start === null && end === null) {
        return "";
    }

    if (start === null) {
        return String(end);
    }

    if (end === null) {
        return String(start);
    }

    return `${start} → ${end}`;
}
